import { queryOptions, useQuery } from "@tanstack/react-query";
import { httpPostGraphql } from "shared/api/httpClient";
import { useComposeFormContext } from "shared/components/message/compose/context";
import { GeoTab, useGeoLocationFormContext } from "shared/components/message/compose/geoAudience/GeoAudienceContext";
import { useFormSubscription } from "shared/hooks/useFormSubscription";
import { maybe } from "shared/utils/maybe";
import { graphql } from "src/gql";

type SelectedSenderFilter = {
    id: string;
    name: string;
    projectedUsersWithin: number;
    geometry: GeoJSON.Polygon;
};

type GeoFilterRecord = {
    id: string;
    boundary: string;
    name: string;
    projectedUsersWithin: number;
};

type SenderFilter = {
    id: string;
    name: string;
    layer: { id: string; name: string };
};

type GeoAudienceEditorState = {
    geoFilters: string[];
    senderFilters: { id: string; name: string; layer: { id: string; name: string } }[];
    senderLayers: SenderFilter["layer"][];
    senderGeoFilters: GeoFilterRecord[];
    tab: GeoTab;
    selectedLayerId: string | null;
    selectedSenderFilters: SelectedSenderFilter[];
    setSelectedLayerId: (value: string | null) => void;
    handleToggleSenderFilter: (filter: SelectedSenderFilter) => void;
    handleTabChange: (value: GeoTab) => void;
    setFeatures: (features: GeoJSON.Feature<GeoJSON.Polygon>[]) => void;
    isLoading: boolean;
};

export const useGeoAudienceEditor = (): GeoAudienceEditorState => {
    const form = useComposeFormContext();
    const geoForm = useGeoLocationFormContext();
    const geoFilters = useFormSubscription(form, "audience.geofilters");
    const senderId = useFormSubscription(form, "sender.id");

    const { data: senderFilters = [], isPending: isSenderFiltersPending } = useQuery(queries.senderFilters(senderId));
    const senderFilterIds = senderFilters.map((filter) => filter.id);

    const { data: senderGeoFilters = [], isPending: isGeoFiltersPending } = useQuery(queries.geoFiltersByIds(senderFilterIds));
    const selectedSenderFilters = geoFilters
        .map((id) =>
            maybe(senderGeoFilters.find((filter) => filter.id === id))?.take((filter) => ({
                id: filter.id,
                name: filter.name,
                projectedUsersWithin: filter.projectedUsersWithin,
                geometry: JSON.parse(filter.boundary),
            })),
        )
        .filter((filter) => !!filter);

    function handleToggleSenderFilter(next: SelectedSenderFilter) {
        const nextIds = geoFilters.includes(next.id) ? geoFilters.filter((id) => id !== next.id) : [...geoFilters, next.id];
        form.setFieldValue("audience.geofilters", nextIds);
    }

    return {
        isLoading: isSenderFiltersPending || isGeoFiltersPending,
        geoFilters,
        senderFilters,
        senderLayers: Array.from(new Map(senderFilters.map((f) => [f.layer.id, f.layer])).values()),
        senderGeoFilters,
        tab: useFormSubscription(geoForm, "tab"),
        selectedLayerId: useFormSubscription(geoForm, "selectedLayerId"),
        selectedSenderFilters,
        setSelectedLayerId: (value) => geoForm.setFieldValue("selectedLayerId", value),
        handleToggleSenderFilter,
        handleTabChange: (value: GeoTab) => geoForm.setFieldValue("tab", value),
        setFeatures: (nextFeatures) => form.setFieldValue("audience.features", nextFeatures),
    };
};

const queries = {
    senderFilters: (senderId: string) =>
        queryOptions({
            enabled: !!senderId,
            queryKey: ["sender", "filters", senderId],
            queryFn: () =>
                httpPostGraphql(
                    graphql(`
                        query GeoAudienceEditorSenderFilters($senderId: UUID!) {
                            sender(id: $senderId) {
                                id
                                filters(limit: 1000, offset: 0) {
                                    id
                                    name
                                    layer {
                                        id
                                        name
                                    }
                                }
                            }
                        }
                    `),
                    { senderId },
                ),
            select: (data) => data.sender?.filters ?? [],
        }),
    geoFiltersByIds: (filterIds: string[]) =>
        queryOptions({
            enabled: filterIds.length > 0,
            queryKey: ["geofilters", "by-ids", [...filterIds].sort().join(",")],
            queryFn: async () => {
                const results = await Promise.all(
                    filterIds.map((id) =>
                        httpPostGraphql(
                            graphql(`
                                query MapSenderBoundaryOverlayGeoFilter($id: UUID!) {
                                    geoFilter(id: $id) {
                                        id
                                        boundary
                                        name
                                        projectedUsersWithin
                                    }
                                }
                            `),
                            { id },
                        ),
                    ),
                );
                return results.map((result) => result.geoFilter).filter((filter) => !!filter);
            },
        }),
};
